'use client';

import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountError,
  useCommerceSource,
  type Order,
  type OrderLookup,
  type OrderReturn,
  type ReturnReason,
} from '@akademi/site-builder-runtime';
import { displayQuantity, isPositive, subtractQuantity, wholeChoices } from './returnQuantities';

const REASONS: ReturnReason[] = ['faulty', 'damaged', 'wrong_item', 'not_required'];

/**
 * The returns part of an order page: what has been sent back and where it has got to, and the
 * form to send something back.
 *
 * <p>The form is offered only for what can still be returned — the backend says so per line, net
 * of what open requests already claim — so a shopper is never invited to ask for something the
 * desk would refuse. A guest's page passes the proof it looked the order up with; a signed-in
 * shopper's needs none.
 */
export function OrderReturns({
  order,
  proof,
  onChange,
}: {
  order: Order;
  proof?: OrderLookup;
  onChange?: (order: Order) => void;
}) {
  const commerce = useCommerceSource();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const locale = i18n.language || 'en';
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<string | null>(null);

  const returnable = (order.lines ?? []).filter((line) => isPositive(line.quantityReturnable));
  const returns = order.returns ?? [];
  const canRequest = Boolean(commerce?.requestReturn) && returnable.length > 0 && Boolean(onChange);

  if (returns.length === 0 && !canRequest && !confirmed) return null;

  function returned(rma: OrderReturn, requested: Map<number, string>) {
    setOpen(false);
    setConfirmed(rma.rmaNumber);
    onChange?.({
      ...order,
      returns: [rma, ...returns],
      lines: (order.lines ?? []).map((line) =>
        requested.has(line.id)
          ? { ...line, quantityReturnable: subtractQuantity(line.quantityReturnable, requested.get(line.id)) }
          : line,
      ),
    });
  }

  return (
    <section aria-labelledby={`returns-${order.orderNumber}`}>
      <h2 id={`returns-${order.orderNumber}`} style={subheadingStyle}>
        {t('commerce.returns.heading')}
      </h2>

      {confirmed && (
        <p role="status" style={successStyle}>
          {t('commerce.returns.requested', { number: confirmed })}
        </p>
      )}

      {returns.length > 0 && (
        <ul style={listStyle}>
          {returns.map((rma) => (
            <ReturnSummary key={rma.rmaNumber} rma={rma} locale={locale} />
          ))}
        </ul>
      )}

      {canRequest &&
        (open ? (
          <ReturnRequestForm
            order={order}
            proof={proof}
            onCancel={() => setOpen(false)}
            onReturned={returned}
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setConfirmed(null);
              setOpen(true);
            }}
            style={secondaryButtonStyle}
          >
            {t('commerce.returns.start')}
          </button>
        ))}
    </section>
  );
}

function ReturnSummary({ rma, locale }: { rma: OrderReturn; locale: string }) {
  const { t } = useTranslation('site-builder-blocks');
  const approved = rma.status === 'APPROVED';
  return (
    <li style={returnStyle}>
      <div style={returnHeaderStyle}>
        <span style={{ fontWeight: 600 }}>{t('commerce.returns.number', { number: rma.rmaNumber })}</span>
        <span style={badgeStyle}>{label(t, 'statuses', rma.status)}</span>
      </div>
      {rma.requestedAt && (
        <p style={mutedTextStyle}>
          {t('commerce.returns.requestedOn', { date: formatDate(rma.requestedAt, locale) })}
        </p>
      )}
      <ul style={{ ...listStyle, margin: '8px 0 0' }}>
        {rma.lines.map((line, index) => (
          <li key={`${line.orderLineId ?? 'x'}-${index}`}>
            {displayQuantity(line.quantity)} × {line.description}
          </li>
        ))}
      </ul>
      {rma.resolution && rma.status === 'COMPLETED' && (
        <p style={{ margin: '8px 0 0', fontWeight: 600 }}>{label(t, 'resolutions', rma.resolution)}</p>
      )}
      {approved && (rma.returnToName || rma.returnCarrier || rma.returnTrackingNumber) && (
        <div style={instructionsStyle}>
          {rma.returnToName && (
            <>
              <p style={{ margin: 0, fontWeight: 600 }}>{t('commerce.returns.sendTo')}</p>
              <p style={{ margin: '4px 0 0', whiteSpace: 'pre-line' }}>
                {rma.returnToName}
                {rma.returnToAddressText ? `\n${rma.returnToAddressText}` : ''}
              </p>
            </>
          )}
          {rma.returnCarrier && (
            <p style={{ margin: '8px 0 0' }}>
              {t('commerce.returns.carrier')}: {rma.returnCarrier}
            </p>
          )}
          {rma.returnTrackingNumber && (
            <p style={{ margin: '4px 0 0' }}>
              {t('commerce.returns.tracking')}: {rma.returnTrackingNumber}
            </p>
          )}
          <p style={{ margin: '8px 0 0' }}>{t('commerce.returns.markParcel', { number: rma.rmaNumber })}</p>
        </div>
      )}
      {rma.status === 'REJECTED' && rma.reasonDetail && (
        <p style={{ margin: '8px 0 0', whiteSpace: 'pre-line' }}>{rma.reasonDetail}</p>
      )}
    </li>
  );
}

function ReturnRequestForm({
  order,
  proof,
  onCancel,
  onReturned,
}: {
  order: Order;
  proof?: OrderLookup;
  onCancel: () => void;
  onReturned: (rma: OrderReturn, requested: Map<number, string>) => void;
}) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('site-builder-blocks');
  const lines = useMemo(
    () => (order.lines ?? []).filter((line) => isPositive(line.quantityReturnable)),
    [order.lines],
  );
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const [reason, setReason] = useState<ReturnReason | ''>('');
  const [detail, setDetail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(lineId: number, max: string, checked: boolean) {
    setChosen((current) => {
      const next = { ...current };
      if (checked) next[lineId] = wholeChoices(max) ? '1' : displayQuantity(max);
      else delete next[lineId];
      return next;
    });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending || !commerce?.requestReturn) return;
    const items = Object.entries(chosen)
      .map(([id, quantity]) => ({ orderLineId: Number(id), quantity }))
      .filter((item) => isPositive(item.quantity));
    if (items.length === 0) {
      setError(t('commerce.returns.errors.chooseItem'));
      return;
    }
    if (!reason) {
      setError(t('commerce.returns.errors.chooseReason'));
      return;
    }
    setSending(true);
    setError(null);
    try {
      const rma = await commerce.requestReturn({
        orderNumber: order.orderNumber,
        reason,
        reasonDetail: detail.trim() || undefined,
        lines: items,
        proof,
      });
      onReturned(rma, new Map(items.map((item) => [item.orderLineId, item.quantity])));
    } catch (failure) {
      const kind = failure instanceof AccountError ? failure.kind : 'UNAVAILABLE';
      setError(
        kind === 'INVALID_INPUT'
          ? t('commerce.returns.errors.invalid')
          : kind === 'NOT_FOUND'
            ? t('commerce.returns.errors.notFound')
            : kind === 'RATE_LIMITED'
              ? t('commerce.returns.errors.rateLimited')
              : t('commerce.returns.errors.unavailable'),
      );
      setSending(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} style={formStyle} noValidate>
      <p style={{ margin: 0 }}>{t('commerce.returns.intro')}</p>
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}

      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>{t('commerce.returns.itemsLegend')}</legend>
        {lines.map((line) => {
          const max = line.quantityReturnable ?? '0';
          const choices = wholeChoices(max);
          const picked = chosen[line.id];
          const checkboxId = `return-line-${line.id}`;
          return (
            <div key={line.id} style={itemStyle}>
              <input
                id={checkboxId}
                type="checkbox"
                checked={picked !== undefined}
                disabled={sending}
                onChange={(event) => toggle(line.id, max, event.target.checked)}
              />
              <label htmlFor={checkboxId} style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{line.description}</span>
                <span style={mutedTextStyle}>
                  {t('commerce.returns.upTo', { max: displayQuantity(max) })}
                </span>
              </label>
              {picked !== undefined &&
                (choices ? (
                  <select
                    aria-label={t('commerce.returns.quantityFor', { item: line.description })}
                    value={picked}
                    disabled={sending}
                    onChange={(event) => setChosen((current) => ({ ...current, [line.id]: event.target.value }))}
                    style={selectStyle}
                  >
                    {choices.map((choice) => (
                      <option key={choice} value={String(choice)}>
                        {choice}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0.001"
                    step="0.001"
                    max={displayQuantity(max)}
                    aria-label={t('commerce.returns.quantityFor', { item: line.description })}
                    value={picked}
                    disabled={sending}
                    onChange={(event) => setChosen((current) => ({ ...current, [line.id]: event.target.value }))}
                    style={{ ...selectStyle, width: 110 }}
                  />
                ))}
            </div>
          );
        })}
      </fieldset>

      <div style={{ display: 'grid', gap: 4 }}>
        <label htmlFor={`return-reason-${order.orderNumber}`} style={fieldLabelStyle}>
          {t('commerce.returns.reason')}
        </label>
        <select
          id={`return-reason-${order.orderNumber}`}
          value={reason}
          disabled={sending}
          onChange={(event) => setReason(event.target.value as ReturnReason | '')}
          style={inputStyle}
        >
          <option value="">{t('commerce.returns.chooseReason')}</option>
          {REASONS.map((value) => (
            <option key={value} value={value}>
              {t(`commerce.returns.reasons.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gap: 4 }}>
        <label htmlFor={`return-detail-${order.orderNumber}`} style={fieldLabelStyle}>
          {t('commerce.returns.detail')}
        </label>
        <textarea
          id={`return-detail-${order.orderNumber}`}
          rows={3}
          maxLength={2000}
          value={detail}
          disabled={sending}
          onChange={(event) => setDetail(event.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button type="submit" disabled={sending} style={primaryButtonStyle}>
          {sending ? t('commerce.returns.submitting') : t('commerce.returns.submit')}
        </button>
        <button type="button" onClick={onCancel} disabled={sending} style={secondaryButtonStyle}>
          {t('commerce.returns.cancel')}
        </button>
      </div>
    </form>
  );
}

function label(t: (key: string) => string, group: 'statuses' | 'resolutions', code: string): string {
  const key = `commerce.returns.${group}.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

const subheadingStyle: CSSProperties = { margin: '0 0 12px', fontSize: 18, fontWeight: 600 };
const listStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 };
const returnStyle: CSSProperties = {
  padding: 16,
  borderRadius: 'var(--radius-md, 8px)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const returnHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  alignItems: 'center',
};
const badgeStyle: CSSProperties = {
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 13,
  background: 'var(--color-surface-muted, #f3f4f6)',
};
const instructionsStyle: CSSProperties = {
  marginTop: 12,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-info-50, #eff6ff)',
  border: '1px solid var(--color-info-200, #bfdbfe)',
};
const mutedTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--color-text-secondary, #6b7280)',
};
const formStyle: CSSProperties = { display: 'grid', gap: 16, marginTop: 12, maxWidth: 560 };
const fieldsetStyle: CSSProperties = { border: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 };
const legendStyle: CSSProperties = { fontWeight: 600, marginBottom: 8, padding: 0 };
const itemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const fieldLabelStyle: CSSProperties = { fontSize: 14, fontWeight: 500 };
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  font: 'inherit',
  background: 'var(--color-surface, #fff)',
};
const selectStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  font: 'inherit',
  background: 'var(--color-surface, #fff)',
};
const primaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
};
const secondaryButtonStyle: CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'transparent',
  color: 'inherit',
  fontWeight: 600,
  border: '1px solid var(--color-border, #d1d5db)',
  cursor: 'pointer',
  font: 'inherit',
  justifySelf: 'start',
};
const successStyle: CSSProperties = {
  margin: '0 0 12px',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-success-50, #f0fdf4)',
  border: '1px solid var(--color-success-200, #bbf7d0)',
};
const errorStyle: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-danger-50, #fef2f2)',
  color: 'var(--color-danger-700, #b91c1c)',
};
